import { SendOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Input, Layout, Tooltip, Upload } from 'antd';
import axios from 'axios';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ConversationContext } from '../contexts/ConversationContext';
import { apiUrl, COLOR } from '../utils/constants';
import ChangeColorModal from './kits/ChangeColorModal';
import Message from './kits/Message';

const { Content, Footer } = Layout;
const { TextArea } = Input;

const ChatLayout = () => {
  const { conversationId } = useParams();
  const {
    conversationState: { conversations, conversation },
    getConversationDetail,
    sendNewMessage,
    markSeenMessages,
  } = useContext(ConversationContext);
  const {
    authState: { user },
  } = useContext(AuthContext);
  const [newMessage, setNewMessage] = useState('');
  const endOfMessagesRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [changeColorModal, setChangeColorModal] = useState(false);
  const [scrollY, setScrollY] = useState(null);
  const conversationDetail = useMemo(
    () =>
      conversations?.find(
        (conversationItem) => conversationItem?.id === conversationId
      ),
    [conversationId, conversations]
  );

  useEffect(() => {
    window.addEventListener('scroll', async () => {
      setScrollY(window.scrollY);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getDetail = async () => {
      if (isLoading) return;
      if (
        scrollY === 0 &&
        conversation?.pagination?.page < conversation?.pagination?.totalPages &&
        conversation?.items?.length < conversation?.pagination?.totalItems &&
        !isLoading &&
        conversationId === window.location.pathname.split('/')[2]
      ) {
        setIsLoading(true);
        await getConversationDetail(conversation?.items[0]?.conversationId);
        setIsLoading(false);
        window.removeEventListener('scroll', null);
      }
    };
    getDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY]);

  useEffect(() => {
    const getDetail = async () => {
      if (conversationId === window.location.pathname.split('/')[2]) {
        await getConversationDetail(conversationId);
        setTimeout(() => {
          endOfMessagesRef?.current?.scrollIntoView();
        }, 100);
      }
    };
    getDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    setTimeout(async () => {
      markSeenMessages(conversationId, conversation?.items);
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.items, conversationId]);

  const submitSendMessage = useCallback(
    (e) => {
      e.preventDefault();
      if (!newMessage) return;
      sendNewMessage(
        conversationDetail?.type === 'CHAT_P2P'
          ? { message: newMessage, conversationId }
          : { message: newMessage, conversationGroupId: conversationId }
      );
      setNewMessage('');
      endOfMessagesRef?.current?.scrollIntoView();
    },
    [conversationDetail?.type, conversationId, newMessage, sendNewMessage]
  );

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      action: `${apiUrl}/media/conversations/${conversationId}`,
      headers: {
        Authorization: axios?.defaults?.headers?.common['Authorization'],
        'x-yumyum-key': `${process.env.REACT_APP_X_YUMYUM_KEY}`,
      },
      showUploadList: false,

      beforeUpload: (file) => {
        const isPNG = file?.type === 'image/jpeg';
        if (!isPNG) {
          console.error(`${file?.name} is not a jpeg file`);
        }
        return isPNG || Upload?.LIST_IGNORE;
      },

      onChange(info) {
        if (info?.file?.status !== 'uploading') {
          console.log(info?.file, info?.fileList);
        }
        if (info?.file?.status === 'done') {
          console.log(`${info?.file?.name} file uploaded successfully`);
        } else if (info?.file?.status === 'error') {
          console.error(`${info?.file?.name} file upload failed.`);
        }
      },
    }),
    [conversationId]
  );

  const handleKeyPress = useCallback(
    (ev) => {
      if (ev?.charCode === 13 && !ev?.shiftKey) {
        submitSendMessage(ev);
      }
    },
    [submitSendMessage]
  );

  return (
    <>
      <ChangeColorModal
        changeColorModal={changeColorModal}
        closeChangeColorModal={() => {
          setChangeColorModal(false);
        }}
        conversationDetail={conversationDetail}
      />
      <Content
        style={{
          overflow: 'initial',
        }}
      >
        {conversation?.items?.length > 0 && (
          <>
            <div
              style={{
                padding: '0px 24px',
                background: '#fff',
                minHeight: '100%',
                marginBottom: '96px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column-reverse' }}>
                {conversation?.items?.map((item, index) => (
                  <div key={item.id}>
                    <Message
                      userId={user?.id}
                      message={item}
                      key={item?.id}
                      openChangeColorModal={() => {
                        setChangeColorModal(true);
                      }}
                      conversationDetail={conversationDetail}
                    />
                    {0 === index &&
                      item?.usersSeen?.map((userSeen) => (
                        <Avatar.Group
                          maxCount={2}
                          maxPopoverTrigger="click"
                          size="small"
                          maxStyle={{
                            color: '#f56a00',
                            backgroundColor: '#fde3cf',
                            cursor: 'pointer',
                          }}
                          style={{
                            float: 'right',
                          }}
                          key={userSeen?.id}
                        >
                          {userSeen?.id !== user?.id && (
                            <Tooltip title={userSeen.fullName} placement="top">
                              <Avatar
                                icon={<UserOutlined />}
                                src={userSeen?.avatar}
                                size="small"
                                key={userSeen?.id}
                              />
                            </Tooltip>
                          )}
                        </Avatar.Group>
                      ))}
                  </div>
                ))}
              </div>
            </div>
            <div ref={endOfMessagesRef} />
          </>
        )}
      </Content>
      <Footer
        style={{
          position: 'fixed',
          bottom: 0,
          width: '-webkit-fill-available',
          display: 'flex',
          padding: '12px 25px',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />} />
        </Upload>
        <TextArea
          placeholder="Type something..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ borderRadius: '32px', width: '85%' }}
          size="large"
          name="newMessage"
          value={newMessage}
          onChange={(e) => setNewMessage(e?.target?.value)}
          onKeyPress={handleKeyPress}
        />
        <Button
          type="primary"
          shape="round"
          icon={<SendOutlined />}
          size="large"
          onClick={submitSendMessage}
          style={
            conversationDetail?.color
              ? {
                  backgroundColor: COLOR[conversationDetail?.color],
                  borderColor: COLOR[conversationDetail?.color],
                }
              : null
          }
        >
          Send
        </Button>
      </Footer>
    </>
  );
};

export default memo(ChatLayout);
